import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../../i18n";

/**
 * Bottom sheet on a phone, centred dialog from tablet up.
 * Everything the register overlays (ticket, history, held sales) uses it.
 *
 * Rendered into document.body: an ancestor with a transform or a backdrop
 * filter becomes the containing block for position: fixed, and a sheet opened
 * from inside one would be laid out against that element instead of the
 * screen. `actions` sit in the header beside the close button.
 */
const Sheet = ({ open, onClose, title, subtitle, actions, children, footer }) => {
  const { t } = useI18n();

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center text-slate-100">
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />

      <div className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] flex flex-col bg-slate-900 border-t sm:border border-slate-700 rounded-t-3xl sm:rounded-3xl shadow-2xl animate-[posSheetIn_.18s_ease-out]">
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-slate-800">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white truncate">{title}</h2>
            {subtitle && (
              <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              className="shrink-0 w-9 h-9 rounded-full bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>

        {footer && (
          <div className="border-t border-slate-800 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Sheet;
